import type { GrowthState, NavigationMode } from '../App';

export type CapturePresetId = 'hero' | 'focus' | 'timeline' | 'privacy' | 'trailer';
export type TrailerWindowCue = 'orientation' | 'controls' | 'details' | 'timeline';

export interface CapturePreset {
  id: CapturePresetId;
  fixedNow: number;
  language: 'en';
  navigationMode: NavigationMode;
  selectedNodeId?: string;
  growth: GrowthState;
  highlightsEnabled: boolean;
  labelsEnabled: boolean;
  hideKeyboard: boolean;
  scriptedTrailer?: boolean;
  durationMs?: number;
}

export interface TrailerCue {
  startMs: number;
  endMs: number;
  nodeId: string;
  navigationMode: NavigationMode;
  window: TrailerWindowCue;
  kicker: string;
  title: string;
  copy: string;
  growth?: GrowthState;
}

const fixedCaptureNow = Date.parse('2026-05-13T12:00:00.000Z');

const fullGrowth: GrowthState = { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false };

export const trailerDurationMs = 20000;

export const trailerCues: TrailerCue[] = [
  {
    startMs: 0,
    endMs: 3300,
    nodeId: 'file:00_Trailer/Your Vault Becomes Visible.md',
    navigationMode: 'overview',
    window: 'orientation',
    kicker: 'Open a vault',
    title: 'Your Vault Becomes Visible',
    copy: 'A public demo vault resolves into folders, notes, links, and living structure.',
    growth: fullGrowth
  },
  {
    startMs: 3300,
    endMs: 6600,
    nodeId: 'file:10_Structure/Clusters And Links.md',
    navigationMode: 'focus',
    window: 'details',
    kicker: 'Read the structure',
    title: 'Clusters And Links',
    copy: 'The inspector turns one note into context: area, status, backlinks, and neighboring ideas.'
  },
  {
    startMs: 6600,
    endMs: 10000,
    nodeId: 'file:20_Live_Work/Recent Changes Pulse.md',
    navigationMode: 'deep',
    window: 'timeline',
    kicker: 'Follow live work',
    title: 'Recent Changes Pulse',
    copy: 'The timeline shows how a vault grows and lets the camera jump to current changes.',
    growth: { enabled: true, playing: false, progress: 0.66, speed: 1.2, focusZoom: true }
  },
  {
    startMs: 10000,
    endMs: 13300,
    nodeId: 'file:30_Focus/Focus Flights.md',
    navigationMode: 'focus',
    window: 'controls',
    kicker: 'Change perspective',
    title: 'Focus Flights',
    copy: 'Overview, focus, depth, and flight modes keep exploration calm instead of chaotic.'
  },
  {
    startMs: 13300,
    endMs: 16600,
    nodeId: 'file:40_Privacy/Private Vaults Stay Local.md',
    navigationMode: 'focus',
    window: 'details',
    kicker: 'Stay local',
    title: 'Private Vaults Stay Local',
    copy: 'The showcase uses only demo data. Real vaults stay on the local machine.'
  },
  {
    startMs: 16600,
    endMs: trailerDurationMs,
    nodeId: 'file:50_Demo/Explore The Static Demo.md',
    navigationMode: 'overview',
    window: 'orientation',
    kicker: 'Try the public demo',
    title: 'Explore The Static Demo',
    copy: 'The same deterministic vault powers screenshots, trailer captures, and GitHub Pages.'
  }
];

const presets: Record<CapturePresetId, CapturePreset> = {
  hero: {
    id: 'hero',
    fixedNow: fixedCaptureNow,
    language: 'en',
    navigationMode: 'overview',
    growth: { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false },
    highlightsEnabled: true,
    labelsEnabled: true,
    hideKeyboard: true
  },
  focus: {
    id: 'focus',
    fixedNow: fixedCaptureNow,
    language: 'en',
    navigationMode: 'focus',
    selectedNodeId: 'file:20_Projects/Vault Kosmos.md',
    growth: { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false },
    highlightsEnabled: true,
    labelsEnabled: true,
    hideKeyboard: true
  },
  timeline: {
    id: 'timeline',
    fixedNow: fixedCaptureNow,
    language: 'en',
    navigationMode: 'deep',
    selectedNodeId: 'file:40_Writing/Demo Walkthrough.md',
    growth: { enabled: true, playing: false, progress: 0.72, speed: 1.2, focusZoom: true },
    highlightsEnabled: true,
    labelsEnabled: true,
    hideKeyboard: true
  },
  privacy: {
    id: 'privacy',
    fixedNow: fixedCaptureNow,
    language: 'en',
    navigationMode: 'focus',
    selectedNodeId: 'file:30_Sources/Local Data Ethics.md',
    growth: { enabled: false, playing: false, progress: 1, speed: 1, focusZoom: false },
    highlightsEnabled: true,
    labelsEnabled: true,
    hideKeyboard: false
  },
  trailer: {
    id: 'trailer',
    fixedNow: fixedCaptureNow,
    language: 'en',
    navigationMode: 'overview',
    selectedNodeId: trailerCues[0].nodeId,
    growth: fullGrowth,
    highlightsEnabled: true,
    labelsEnabled: true,
    hideKeyboard: true,
    scriptedTrailer: true,
    durationMs: trailerDurationMs
  }
};

export function capturePresetFromLocation(location: Location = window.location): CapturePreset | undefined {
  const params = new URLSearchParams(location.search);
  const raw = params.get('capture');
  if (!raw) return undefined;
  return presets[raw as CapturePresetId] ?? presets.hero;
}

export function isCaptureMode(location: Location = window.location): boolean {
  return Boolean(capturePresetFromLocation(location));
}
